<template>
  <v-container fluid>
    <v-layout text-xs-center wrap>
      <v-flex>
        <oxford-placement-tests class="mb-4" />
      </v-flex>
      <filters
        v-if="students"
        :params="params"
        :filterStudents="filterStudents"
      />
      <v-flex>
        <data-table
          :students="filteredStudents"
          :loading="loading"
          :exportToCsv="exportToCsv"
          :exportCompletion="exportCompletion"
          :exportIssues="exportIssues"
          :openModal="openModal"
          :openGroupReminderModal="openGroupReminderModal"
        />
      </v-flex>
      <graphs :stats="stats" v-if="students" />
      <snackbar
        :id="snackbar.id"
        :type="snackbar.type"
        :message="snackbar.message"
      />
      <reminder-modal
        :open.sync="reminderModalOpen"
        :closeModal="closeModal"
        :sendReminder="sendReminder"
        :student="modalStudent"
        :sending="sending"
      />
      <group-reminder-modal
        :open.sync="groupReminderModalOpen"
        :closeModal="closeModal"
        :sendReminders="sendReminders"
        :sending="sending"
      />
      <v-dialog
        v-model="exportInProgress"
        persistent
        width="300"
      >
        <v-card
          color="teal lighten-1"
          dark
        >
          <v-card-text>
            <p>Le téléchargement est en cours, veuillez patienter...</p>
            <p>Cela peut prendre quelques minutes</p>
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-layout>
  </v-container>
</template>

<script>
import DataTable from '@/components/DataTable';
import Graphs from '@/components/Graphs';
import Snackbar from '@/components/Snackbar';
import Filters from '@/components/Filters';
import ReminderModal from '@/components/ReminderModal';
import GroupReminderModal from '@/components/GroupReminderModal';
import { getPercentage } from '../lib/utils';
import axios from 'axios';
import uuid from 'uuid';
import OxfordPlacementTests from '../components/OxfordPlacementTests.vue';

export default {
  components: {
    DataTable,
    Graphs,
    Snackbar,
    Filters,
    ReminderModal,
    GroupReminderModal,
    OxfordPlacementTests,
  },
  data: () => ({
    students: undefined,
    filteredStudents: undefined,
    loading: false,
    reminderModalOpen: false,
    groupReminderModalOpen: false,
    sending: false,
    modalStudent: undefined,
    snackbar: {
      type: '',
      message: '',
      id: '',
    },
    exportInProgress: false,
  }),
  async mounted() {
    this.loading = true;
    try {
      const { data: students } = await axios({
        method: 'GET',
        url: `${process.env.VUE_APP_API_URI}/students`,
      });
      this.students = students;
      this.filteredStudents = students;
      this.loading = false;
    } catch (e) {
      this.loading = false;
      this.snackbar = {
        id: uuid(),
        type: 'error',
        message: 'Erreur de chargement',
      };
    }
  },
  computed: {
    stats: function () {
      const password = getPercentage(this.students, 'hasEduID');
      const ahes = getPercentage(this.students, 'ahesFin');
      const codeOfConduct = getPercentage(
        this.students,
        'codeOfConductAccepted'
      );
      const equivalency = getPercentage(
        this.students,
        'equivalencySubmittedWish'
      );
      const langEquivalency = getPercentage(
        this.students,
        'langEquivalencySubmitted'
      );
      return {
        password,
        ahes,
        codeOfConduct,
        equivalency,
        langEquivalency,
      };
    },
    params: function () {
      const departements = [
        ...new Set(this.students.map((s) => s.departement)),
      ].sort();
      const modes = [...new Set(this.students.map((s) => s.mode))].sort();
      const filieres = [...new Set(this.students.map((s) => s.filiere))].sort();
      return {
        departements,
        modes,
        filieres,
      };
    },
  },
  methods: {
    sendReminders: async function (selectedReminder, vector) {
      this.sending = true;
      try {
        const { data: nbSent } = await axios({
          method: 'POST',
          url: `${process.env.VUE_APP_API_URI}/reminders?vector=${vector}`,
          data: [selectedReminder],
        });
        this.sending = false;
        this.snackbar = {
          id: uuid(),
          type: 'success',
          message: `${nbSent} relances envoyées`,
        };
        this.groupReminderModalOpen = false;
      } catch (e) {
        this.snackbar = {
          id: uuid(),
          type: 'error',
          message: 'Erreur de chargement',
        };
        this.sending = false;
      }
    },
    sendReminder: async function (selectedReminders, vector) {
      this.sending = true;
      try {
        await axios({
          method: 'POST',
          url: `${process.env.VUE_APP_API_URI}/students/${this.modalStudent._id}/reminders?vector=${vector}`,
          data: selectedReminders,
        });
        this.sending = false;
        this.snackbar = {
          id: uuid(),
          type: 'success',
          message: 'Relance envoyée',
        };
        this.reminderModalOpen = false;
      } catch (e) {
        this.snackbar = {
          id: uuid(),
          type: 'error',
          message: 'Erreur de chargement',
        };
        this.sending = false;
      }
    },
    closeModal: function () {
      this.reminderModalOpen = false;
      this.groupReminderModalOpen = false;
      this.modalStudent = undefined;
    },
    openModal: function (student) {
      this.modalStudent = student;
      this.reminderModalOpen = true;
    },
    openGroupReminderModal: function () {
      this.groupReminderModalOpen = true;
    },
    filterStudents: function ({ mode, departement, filiere }) {
      const modeFilter = mode ? (s) => s.mode === mode : (s) => s;
      const departementFilter = departement
        ? (s) => s.departement === departement
        : (s) => s;
      const filiereFilter = filiere ? (s) => s.filiere === filiere : (s) => s;
      this.filteredStudents = this.students
        .filter(departementFilter)
        .filter(modeFilter)
        .filter(filiereFilter);
    },
    exportToCsv: async function () {
      try {
        this.exportInProgress = true;
        const { data } = await axios({
          method: 'GET',
          url: `${process.env.VUE_APP_API_URI}/reports`,
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'students.xlsx');
        document.body.appendChild(link);
        link.click();
      } catch (e) {
        this.snackbar = {
          id: uuid(),
          type: 'error',
          message: 'Erreur lors de la génération du rapport',
        };
      } finally {
        this.exportInProgress = false;
      }
    },
    exportCompletion: async function () {
      try {
        this.exportInProgress = true;
        const { data } = await axios({
          method: 'GET',
          url: `${process.env.VUE_APP_API_URI}/completion-report`,
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'completionReport.xlsx');
        document.body.appendChild(link);
        link.click();
      } catch (e) {
        this.snackbar = {
          id: uuid(),
          type: 'error',
          message: 'Erreur lors de la génération du rapport',
        };
      } finally {
        this.exportInProgress = false;
      }
    },
    exportIssues: async function () {
      try {
        const { data } = await axios({
          method: 'GET',
          url: `${process.env.VUE_APP_API_URI}/issues`,
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'issues.xlsx');
        document.body.appendChild(link);
        link.click();
      } catch (e) {
        this.snackbar = {
          id: uuid(),
          type: 'error',
          message: 'Erreur de chargement',
        };
      }
    },
  },
};
</script>
